<template>
    <div class="character-configurator">
        <div class="mb-3" v-if="!isReadonly">
            <button
                type="button"
                class="btn btn-sm btn-success"
                @click="addCharacter"
            >
                <i class="fa fa-plus"></i> Add Character
            </button>
        </div>

        <div
            v-for="(character, index) in characters"
            :key="index"
            class="card mb-3 character-card"
        >
            <div class="card-header">
                <div class="d-flex justify-content-between align-items-center">
                    <div class="d-flex align-items-center cursor-pointer" @click="toggleCharacter(index)">
                        <i
                            :class="character.isExpanded
                                ? 'fa fa-chevron-down me-2 mr-2'
                                : 'fa fa-chevron-right me-2 mr-2'"
                        ></i>
                        <h5 class="mb-0">Character {{ index + 1 }}: {{ character.name || 'Unnamed' }}</h5>
                    </div>
                    <button
                        type="button"
                        class="btn btn-sm btn-danger"
                        @click="removeCharacter(index)"
                        v-if="characters.length > 1 && !isReadonly"
                    >
                        <i class="fa fa-trash"></i> Remove
                    </button>
                </div>
            </div>

            <div class="card-body" v-show="character.isExpanded">
                <div class="form-group mb-3">
                    <label :for="`character-name-${index}`">Character Name</label>
                    <input
                        :id="`character-name-${index}`"
                        type="text"
                        class="form-control"
                        v-model="character.name"
                        placeholder="e.g., Dr. Sarah"
                        :disabled="isReadonly"
                        required
                    />
                    <input type="hidden" :name="`characters[${index}][name]`" :value="character.name" />
                </div>

                <div class="form-group mb-3">
                    <label>Voice Configuration</label>
                    <voice-selector
                        :id="character.voice.voiceName"
                        :id-index="`${index}`"
                        :assistant-provider="currentProvider"
                        :speed="character.voice.speed"
                        :model-id="character.voice.opts?.model_id"
                        :stability="character.voice.opts?.stability"
                        :similarity-boost="character.voice.opts?.similarity_boost"
                        :style-exaggeration="character.voice.opts?.style_exaggeration"
                        :use-speaker-boost="character.voice.opts?.use_speaker_boost"
                        :instructions="character.voice.opts?.instructions"
                        :pronunciation-dictionary-id="
                            dictionaryLocators(character.voice)[0]?.pronunciation_dictionary_id"
                        :pronunciation-dictionary-version-id="
                            dictionaryLocators(character.voice)[0]?.version_id"
                        :custom-pronunciations="character.voice.opts?.custom_pronunciations"
                        @handleVoiceChange="updateVoice(index, $event)"
                    ></voice-selector>

                    <input type="hidden" :name="`characters[${index}][voice][provider]`"
                           :value="character.voice.provider" />
                    <input type="hidden" :name="`characters[${index}][voice][voiceName]`"
                           :value="character.voice.voiceName" />
                    <input type="hidden" :name="`characters[${index}][voice][speed]`" :value="character.voice.speed" />
                    <template v-for="(optValue, optKey) in scalarVoiceOpts(character.voice.opts)"
                              :key="`${index}-opt-${optKey}`">
                        <input type="hidden" :name="`characters[${index}][voice][opts][${optKey}]`"
                               :value="hiddenOptValue(optValue)" />
                    </template>
                    <template v-for="(locator, locatorIndex) in dictionaryLocators(character.voice)"
                              :key="`${index}-locator-${locatorIndex}`">
                        <input type="hidden"
                               :name="`characters[${index}][voice][opts][pronunciation_dictionary_locators]`
                                   + `[${locatorIndex}][pronunciation_dictionary_id]`"
                               :value="locator.pronunciation_dictionary_id" />
                        <input v-if="locator.version_id" type="hidden"
                               :name="`characters[${index}][voice][opts][pronunciation_dictionary_locators]`
                                   + `[${locatorIndex}][version_id]`"
                               :value="locator.version_id" />
                    </template>
                </div>

                <div class="form-group mb-3">
                    <label :for="`character-alias-${index}`">Alias</label>
                    <input
                        :id="`character-alias-${index}`"
                        type="text"
                        class="form-control"
                        v-model="character.alias"
                        placeholder="e.g., senior_executive"
                        :disabled="isReadonly"
                        required
                    />
                    <input type="hidden" :name="`characters[${index}][alias]`" :value="character.alias" />
                </div>

                <div class="form-group mb-3">
                    <div class="d-flex align-items-center mb-2">
                        <label class="mb-0 me-2">Emotional States</label>
                        <button
                            type="button"
                            class="btn btn-sm btn-link p-0 text-decoration-none fs-5 lh-1"
                            @click="toggleEmotions(index)"
                        >
                            <i :class="character.showEmotions ? 'fa fa-minus-circle' : 'fa fa-plus-circle'"></i>
                        </button>
                    </div>

                    <div v-show="character.showEmotions" class="emotion-config">
                        <button
                            v-if="!isReadonly"
                            type="button"
                            class="btn btn-sm btn-primary mb-3"
                            @click="addEmotion(index)"
                        >
                            <i class="fa fa-plus"></i> Add Emotion
                        </button>

                        <div class="emotions-list">
                            <div
                                v-for="(emotion, emotionIndex) in character.emotions"
                                :key="emotion.key"
                                class="emotion-item p-3 mb-2 border rounded"
                            >
                                <div class="d-flex align-items-center justify-content-between mb-1">
                                    <h6 class="text-capitalize mb-0 d-flex align-items-center flex-grow-1">
                                        <span v-if="emotion.name === CHARACTER_EMOTIONS.NEUTRAL ||
                                            emotion.name === CHARACTER_EMOTIONS.THINKING">
                                            {{ emotion.name }}
                                            <span class="badge badge-info ml-2" v-if="emotion.name ===
                                                CHARACTER_EMOTIONS.NEUTRAL">Required</span>
                                        </span>
                                        <input
                                            v-else
                                            type="text"
                                            class="form-control form-control-sm emotion-name-input"
                                            :value="emotion.name"
                                            :placeholder="`emotion ${emotionIndex + 1}`"
                                            :disabled="isReadonly"
                                            :ref="el => registerEmotionInput(index, emotionIndex, el)"
                                            @keydown.enter.prevent="$event.target.blur()"
                                            @blur="commitEmotionRename(index, emotionIndex, $event.target)"
                                        />
                                    </h6>
                                    <button
                                        v-if="!isReadonly"
                                        type="button"
                                        class="btn btn-sm btn-outline-danger ms-2"
                                        @click="deleteEmotion(index, emotionIndex)"
                                        :disabled="emotion.name === CHARACTER_EMOTIONS.NEUTRAL
                                            || emotion.name === CHARACTER_EMOTIONS.THINKING"
                                        :title="protectedEmotionTitle(emotion.name)"
                                    >
                                        <i class="fa fa-trash"></i>
                                    </button>
                                </div>

                                <small class="text-muted">
                                    {{ emotion.images.length }} image{{ emotion.images.length !== 1 ? 's' : '' }}
                                </small>

                                <emotion-images-field
                                    :images="emotion.images"
                                    :name="emotion.name ? `characters[${index}][emotions][${emotion.name}]` : ''"
                                    @update:images="updateEmotionImages(index, emotionIndex, $event)"
                                ></emotion-images-field>
                                <input
                                    v-if="emotion.name && emotion.name !== CHARACTER_EMOTIONS.THINKING
                                        && emotion.images.length === 0"
                                    type="hidden"
                                    :name="`characters[${index}][emotions][${emotion.name}]`" :value="[]"/>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div v-if="characters.length === 0" class="alert alert-info">
            <i class="fa fa-info-circle"></i> No characters configured. Click "Add Character" to get started.
        </div>

        <confirm-modal
            modal-id="confirmDeleteCharacter"
            title="Delete Character"
            :message="confirmDeleteMessage"
            warning="This action cannot be undone."
            confirm-text="Delete"
            variant="danger"
            @confirm="confirmDeleteCharacter"
        ></confirm-modal>

        <confirm-modal
            modal-id="confirmDeleteEmotion"
            title="Delete Emotion"
            :message="confirmDeleteEmotionMessage"
            warning="This action cannot be undone."
            confirm-text="Delete"
            variant="danger"
            @confirm="confirmDeleteEmotion"
        ></confirm-modal>

        <warning-modal
            modal-id="warningModal"
            title="Cannot Delete Emotion"
            message="The &quot;neutral&quot; emotion is required and cannot be deleted."
            details="Every character must have a neutral emotional state."
            button-text="OK"
            variant="warning"
        ></warning-modal>
    </div>
</template>

<script>
import {CHARACTER_EMOTIONS} from '../../common/characterEmotions';
import ConfirmModal from './ConfirmModal.vue';
import EmotionImagesField from './EmotionImagesField.vue';
import VoiceSelector from './VoiceSelector.vue';
import WarningModal from './WarningModal.vue';

export default {
    name: 'CharacterConfigurator',
    components: {
        VoiceSelector,
        EmotionImagesField,
        ConfirmModal,
        WarningModal,
    },
    props: {
        initialCharacters: {
            type: Array,
            default: () => [],
        },
        readonly: {
            type: Boolean,
            default: false,
        },
        imageUrlMap: {
            type: Object,
            default: () => ({}),
        },
        currentProvider: {
            type: String,
            default: null,
        },
    },
    data() {
        return {
            characters: [],
            pendingDeleteIndex: null,
            pendingDeleteEmotion: null,
            scenePackageUploaded: false,
            emotionKeyCounter: 0,
            CHARACTER_EMOTIONS: CHARACTER_EMOTIONS,
        };
    },
    created() {
        this.emotionInputs = new Map();
    },
    computed: {
        isReadonly() {
            return this.readonly || this.scenePackageUploaded;
        },
        confirmDeleteMessage() {
            if (this.pendingDeleteIndex === null) return '';
            const character = this.characters[this.pendingDeleteIndex];
            const characterName = character?.name || 'Unnamed';
            return `Delete Character ${this.pendingDeleteIndex + 1}: ${characterName}?`;
        },
        confirmDeleteEmotionMessage() {
            if (!this.pendingDeleteEmotion) return '';
            const {charIndex, emotionIndex} = this.pendingDeleteEmotion;
            const emotion = this.characters[charIndex]?.emotions[emotionIndex];
            if (!emotion) return '';
            const imageCount = emotion.images.length;
            const label = emotion.name || 'this emotion';
            return imageCount > 0
                ? `Delete "${label}" and its ${imageCount} image(s)?`
                : `Delete "${label}" emotion?`;
        },
    },
    mounted() {
        this.initializeCharacters();
        this._onScenePackageChanged = (e) => {
            this.scenePackageUploaded = e.detail.hasScenePackage;
        };
        window.addEventListener('scene-package-changed', this._onScenePackageChanged);
    },
    beforeUnmount() {
        if (this._onScenePackageChanged) {
            window.removeEventListener('scene-package-changed', this._onScenePackageChanged);
        }
    },
    methods: {
        initializeCharacters() {
            if (this.initialCharacters && this.initialCharacters.length > 0) {
                this.characters = this.initialCharacters.map(char => {
                    const sourceEmotions = char.emotions && typeof char.emotions === 'object'
                        ? char.emotions
                        : {};

                    if (!sourceEmotions[CHARACTER_EMOTIONS.NEUTRAL]) {
                        sourceEmotions[CHARACTER_EMOTIONS.NEUTRAL] = [];
                    }

                    if (!sourceEmotions[CHARACTER_EMOTIONS.THINKING]) {
                        sourceEmotions[CHARACTER_EMOTIONS.THINKING] = [];
                    }

                    const emotions = Object.entries(sourceEmotions).map(([name, images]) => ({
                        key: this.nextEmotionKey(),
                        name,
                        images: Array.isArray(images) ? images : [],
                    }));

                    const voice = char.voice || this.createEmptyVoice();
                    const normalizedVoice = {
                        provider: voice.provider || 'openai',
                        voiceName: voice.voiceName || 'alloy',
                        speed: parseFloat(voice.speed) || 1.0,
                        opts: {
                            ...(voice.opts || {}),
                            model_id: voice.opts?.model_id || undefined,
                            stability: voice.opts?.stability !== undefined ?
                                parseFloat(voice.opts.stability) : undefined,
                            similarity_boost: voice.opts?.similarity_boost !== undefined ?
                                parseFloat(voice.opts.similarity_boost) : undefined,
                            style_exaggeration: voice.opts?.style_exaggeration !== undefined ?
                                parseFloat(voice.opts.style_exaggeration) : undefined,
                            use_speaker_boost: voice.opts?.use_speaker_boost !== undefined ?
                                (voice.opts.use_speaker_boost === true
                                    || voice.opts.use_speaker_boost === 'true') : undefined,
                            instructions: voice.opts?.instructions || undefined,
                            pronunciation_dictionary_locators: this.normalizeDictionaryLocators(
                                voice.opts?.pronunciation_dictionary_locators,
                            ),
                            custom_pronunciations: this.normalizeCustomPronunciations(
                                voice.opts?.custom_pronunciations,
                            ),
                        },
                    };

                    return {
                        name: char.name || '',
                        alias: char.alias || '',
                        voice: normalizedVoice,
                        emotions,
                        showEmotions: true,
                        isExpanded: true,
                    };
                });
            } else {
                this.addCharacter();
            }
        },

        nextEmotionKey() {
            this.emotionKeyCounter += 1;
            return `emotion-${this.emotionKeyCounter}`;
        },

        createEmptyVoice() {
            return {
                provider: 'openai',
                voiceName: 'alloy',
                speed: 1.0,
                opts: {},
            };
        },

        createEmptyEmotions() {
            return [{
                key: this.nextEmotionKey(),
                name: CHARACTER_EMOTIONS.NEUTRAL,
                images: [],
            },{
                key: this.nextEmotionKey(),
                name: CHARACTER_EMOTIONS.THINKING,
                images: [],
            }];
        },

        addCharacter() {
            this.characters.push({
                name: '',
                alias: '',
                voice: this.createEmptyVoice(),
                emotions: this.createEmptyEmotions(),
                showEmotions: true,
                isExpanded: true,
            });
        },

        removeCharacter(index) {
            this.pendingDeleteIndex = index;
            this.$nextTick(() => {
                window.$('#confirmDeleteCharacter').modal('show');
            });
        },

        confirmDeleteCharacter() {
            if (this.pendingDeleteIndex !== null) {
                this.characters.splice(this.pendingDeleteIndex, 1);
                this.pendingDeleteIndex = null;
            }
        },

        normalizeDictionaryLocators(locators) {
            if (!Array.isArray(locators)) {
                return undefined;
            }

            const normalized = locators
                .filter(locator => locator && typeof locator === 'object')
                .map(locator => {
                    const dictionaryId = String(locator.pronunciation_dictionary_id || '').trim();
                    if (!dictionaryId) {
                        return null;
                    }

                    const versionId = String(locator.version_id || '').trim();
                    return versionId
                        ? {pronunciation_dictionary_id: dictionaryId, version_id: versionId}
                        : {pronunciation_dictionary_id: dictionaryId};
                })
                .filter(Boolean);

            return normalized.length ? normalized : undefined;
        },

        normalizeCustomPronunciations(value) {
            let rows = value;
            if (typeof rows === 'string') {
                try {
                    rows = JSON.parse(rows);
                } catch {
                    return undefined;
                }
            }

            if (!Array.isArray(rows)) {
                return undefined;
            }

            const normalized = rows
                .filter(row => row && typeof row === 'object' && row.phrase && row.pronunciation)
                .map(row => ({
                    phrase: String(row.phrase),
                    pronunciation: String(row.pronunciation),
                    phonetic_encoding: row.phonetic_encoding || 'PHONETIC_ENCODING_IPA',
                }));

            return normalized.length ? normalized : undefined;
        },

        scalarVoiceOpts(opts) {
            // Locators are a nested array; the flat hidden-input loop would serialize
            // them as "[object Object]", so they get their own inputs below.
            return Object.fromEntries(
                Object.entries(opts || {}).filter(([key]) => key !== 'pronunciation_dictionary_locators'),
            );
        },

        hiddenOptValue(value) {
            return typeof value === 'object' && value !== null ? JSON.stringify(value) : value;
        },

        dictionaryLocators(voice) {
            return voice?.opts?.pronunciation_dictionary_locators || [];
        },

        updateVoice(index, voiceConfig) {
            if (voiceConfig && typeof voiceConfig === 'object') {

                let provider = 'openai';
                let voiceName = 'alloy';

                if (voiceConfig.voiceId) {
                    const parts = voiceConfig.voiceId.split('_');
                    if (parts.length >= 2) {
                        provider = parts[0];
                        voiceName = parts.slice(1).join('_');
                    }
                }

                this.characters[index].voice = {
                    provider: provider,
                    voiceName: voiceName,
                    speed: voiceConfig.speed || 1.0,
                    opts: voiceConfig.opts || {},
                };
            }
        },

        protectedEmotionTitle(name) {
            if (name === CHARACTER_EMOTIONS.NEUTRAL) {
                return 'Neutral emotion cannot be deleted';
            }
            if (name === CHARACTER_EMOTIONS.THINKING) {
                return 'Thinking emotion cannot be deleted';
            }
            return 'Delete emotion';
        },

        toggleEmotions(index) {
            this.characters[index].showEmotions = !this.characters[index].showEmotions;
        },

        toggleCharacter(index) {
            this.characters[index].isExpanded = !this.characters[index].isExpanded;
        },

        addEmotion(charIndex) {
            const character = this.characters[charIndex];
            const newEmotion = {
                key: this.nextEmotionKey(),
                name: '',
                images: [],
            };
            character.emotions.push(newEmotion);

            this.$nextTick(() => {
                const input = this.emotionInputs.get(newEmotion.key);
                input?.focus();
            });
        },

        registerEmotionInput(charIndex, emotionIndex, el) {
            const emotion = this.characters[charIndex]?.emotions[emotionIndex];
            if (!emotion) return;
            if (el) {
                this.emotionInputs.set(emotion.key, el);
            } else {
                this.emotionInputs.delete(emotion.key);
            }
        },

        commitEmotionRename(charIndex, emotionIndex, inputEl) {
            const character = this.characters[charIndex];
            const emotion = character.emotions[emotionIndex];
            if (!emotion || emotion.name === CHARACTER_EMOTIONS.NEUTRAL ||
                emotion.name === CHARACTER_EMOTIONS.THINKING) {
                return;
            }

            const previous = emotion.name;
            const trimmed = inputEl.value.trim().toLowerCase();

            if (!trimmed) {
                inputEl.value = previous;
                return;
            }

            const conflict = character.emotions.some((e, i) => i !== emotionIndex && e.name === trimmed);
            if (conflict) {
                inputEl.value = previous;
                return;
            }

            emotion.name = trimmed;
        },

        updateEmotionImages(charIndex, emotionIndex, images) {
            const emotion = this.characters[charIndex].emotions[emotionIndex];
            if (emotion) {
                emotion.images = images;
            }
        },

        deleteEmotion(charIndex, emotionIndex) {
            const emotion = this.characters[charIndex]?.emotions[emotionIndex];
            if (!emotion) return;

            if (emotion.name === CHARACTER_EMOTIONS.NEUTRAL || emotion.name === CHARACTER_EMOTIONS.THINKING) {
                this.$nextTick(() => {
                    window.$('#warningModal').modal('show');
                });
                return;
            }

            if (!emotion.name && emotion.images.length === 0) {
                this.characters[charIndex].emotions.splice(emotionIndex, 1);
                return;
            }

            this.pendingDeleteEmotion = {charIndex, emotionIndex};
            this.$nextTick(() => {
                window.$('#confirmDeleteEmotion').modal('show');
            });
        },

        confirmDeleteEmotion() {
            if (this.pendingDeleteEmotion) {
                const {charIndex, emotionIndex} = this.pendingDeleteEmotion;
                this.characters[charIndex].emotions.splice(emotionIndex, 1);
                this.pendingDeleteEmotion = null;
            }
        },
    },
};
</script>

<style scoped>
.character-configurator {
  width: 100%;
}

.character-card {
  border-left: 4px solid #007bff;
}

.emotion-item {
  background-color: #f8f9fa;
}

.emotion-name-input {
  border: 1px solid transparent;
  background: transparent;
  font-size: 1rem;
  font-weight: 500;
  padding: 0.125rem 0.375rem;
  max-width: 240px;
  text-transform: capitalize;
}

.emotion-name-input:hover:not(:disabled),
.emotion-name-input:focus {
  border-color: #ced4da;
  background: #fff;
  outline: none;
}
</style>
